import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";

const STAGES = [
  "Analyzing your business & local market",
  "Writing SEO copy with AI (English)",
  "Picking HD photos from our library",
  "Designing 3 layout options for you",
  "Assembling your mobile-ready website",
];

export default function Generating() {
  const location = useLocation();
  const navigate = useNavigate();
  const form = location.state?.form;
  const [stage, setStage] = useState(0);
  const started = useRef(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!form) { navigate("/create"); return; }

    intervalRef.current = setInterval(() => {
      setStage((s) => Math.min(s + 1, STAGES.length - 1));
    }, 900);

    if (!started.current) {
      started.current = true;
      (async () => {
        try {
          const { data } = await api.post("/sites/generate", form);
          clearInterval(intervalRef.current);
          setStage(STAGES.length - 1);
          setTimeout(() => navigate(`/preview/${data.site.id}`), 700);
        } catch (e) {
          clearInterval(intervalRef.current);
          toast.error(e.response?.data?.detail || "Generation failed. Please try again.");
          navigate("/create");
        }
      })();
    }

    return () => clearInterval(intervalRef.current);
  }, [form, navigate]);

  const pct = Math.round(((stage + 1) / STAGES.length) * 100);

  return (
    <div className="min-h-screen flex items-center justify-center px-5"
      style={{ background: "radial-gradient(ellipse at top, rgba(201,162,75,0.13), transparent 60%), #0A0A0A" }}>
      <div className="w-full max-w-md text-center">
        <div className="flex justify-center mb-8"><Logo size={46} showText={false} /></div>

        {/* Ring */}
        <div className="relative w-40 h-40 mx-auto mb-8">
          <svg className="w-40 h-40 -rotate-90" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r="52" fill="none" stroke="#27272A" strokeWidth="8" />
            <circle cx="60" cy="60" r="52" fill="none" stroke="url(#g)" strokeWidth="8" strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 52}
              strokeDashoffset={2 * Math.PI * 52 * (1 - pct / 100)}
              style={{ transition: "stroke-dashoffset .7s ease" }} />
            <defs>
              <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#C9A24B" />
                <stop offset="100%" stopColor="#E5C378" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-display text-4xl font-bold text-[#E5C378]" data-testid="generator-progress-bar">{pct}%</span>
          </div>
        </div>

        <h1 className="font-display text-2xl font-bold mb-1">Building your website</h1>
        <p className="text-zinc-500 text-sm mb-8">Our AI is doing the heavy lifting. Under a minute.</p>

        <div className="text-left space-y-3">
          {STAGES.map((s, i) => (
            <div key={s} data-testid={`generator-status-step-${i}`}
              className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${i <= stage ? "border-[#C9A24B]/30 bg-[#C9A24B]/5" : "border-[#27272A] bg-[#161616] opacity-50"}`}>
              <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${i < stage ? "bg-[#C9A24B] text-[#0A0A0A]" : i === stage ? "bg-[#C9A24B]/20" : "bg-[#27272A]"}`}>
                {i < stage ? <Check className="w-4 h-4" /> : i === stage ? <Loader2 className="w-4 h-4 text-[#E5C378] wt-spin" /> : null}
              </div>
              <span className={`text-sm ${i <= stage ? "text-[#F5F5F7]" : "text-zinc-500"}`}>{s}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
